/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import Highcharts from 'highcharts-pack/highcharts/highcharts';
import { IAxisItem } from 'src/adhoc/store/types/StateTypes';
import datasetTypes from '../../enums/datasetTypes';
import GenericChartVisualization from './chart/GenericChartVisualization';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import {
    getMeasures,
    getTooltipValue
} from './chart/util/GenericSeriesUtil';
import { getOutputParamsKeyFromField, getOutputParamFromMeasure } from './chart/util/EventUtil';

export class DualMeasureTreeMapVisualization extends GenericChartVisualization {

    useShadow = false

    getChartSection() {
        const chart = super.getChartSection();

        return {
            ...chart,
            type: 'treemap',
            renderTo: 'chartContainer',
            zoomType: 'xy'
        };
    }

    // eslint-disable-next-line class-methods-use-this
    getColorAxisSection() {
        return {
            minColor: '#FFFFFF',
            maxColor: Highcharts.getOptions().colors[0],
            maxPadding: 2,
            tickWidth: 1
        };
    }

    getLegendSection() {
        const section = super.getLegendSection();

        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        const secondMeasureLabel = measures[1].label || '';

        return {
            ...section,
            backgroundColor: 'rgba(255,255,255,0.85)',
            padding: 15,
            symbolWidth: undefined,
            symbolHeight: undefined,
            title: {
                text: secondMeasureLabel
            }
        };
    }

    // eslint-disable-next-line class-methods-use-this
    getTooltipSection() {

        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);

        const getTooltipProps = {
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        return {
            valueDecimals: 2,
            useHTML: true,
            style: {
                fontSize: '1em'
            },
            formatter() {
                const measurePrefix = '<span style="color:{point.color}">\u25CF</span>';
                const chartPoint: any = this;
                const measure1Value = chartPoint.point.value,
                    measure2Value = chartPoint.point.colorValue;
                const { measure1Name, measure2Name } = chartPoint.point;

                const measure1TooltipValue = getTooltipValue(getTooltipProps, measure1Value, measures[0]);
                const measure2TooltipValue = getTooltipValue(getTooltipProps, measure2Value, measures[1]);

                return `${chartPoint.key}</br>${measurePrefix} ${measure1Name}: <b>${measure1TooltipValue}</b></br>${measurePrefix} ${measure2Name}: <b>${measure2TooltipValue}</b>`;
            }
        };
    }

    getTotalIndex(currentTotalNode: any): number {
        if (currentTotalNode.index !== -1) {
            return currentTotalNode.index;
        }
        if (currentTotalNode.total) {
            return this.getTotalIndex(currentTotalNode.total);
        }
        return -1;
    }

    getSeriesSection(selectedColIndex: number, selectedRowIndex: number) {

        const dataset = this.getDataset();
        const staticProperties = this.getStaticProperties();
        const linkOptions = this.getLinkOptions();
        const data: any[] = [];
        const measures = getMeasures(staticProperties.levelsStructure);
        let
            firstMinMaxRecorded = false,
            min :number = 0,
            max :number = 0,
            rowIndex = 0;

        dataset.data.rows.children.forEach((child: any) => {
            const { index } = child;
            let { label } = child;

            // empty names and tooltips causes regressions so we change them to blank strings (see JS-65082)
            if (label === '' || label === null) {
                label = ' ';
            }

            const value = parseFloat(dataset.data.aggregations[index][0]);
            if (!firstMinMaxRecorded) {
                min = value;
                max = value;
                firstMinMaxRecorded = true;
            } else {
                min = Math.min(value, min);
                max = Math.max(value, max);
            }

            const nodeDefinition: any = {
                id: `row_${rowIndex}`,
                name: label,
                // this chart has always two measures
                measure1Name: measures[0].label,
                measure2Name: measures[1].label,
                value,
                colorValue: parseFloat(dataset.data.aggregations[index][1]),
                linkOutputParams: {
                    values: [{
                        key: 'Measures',
                        value: [(measures[0] as IAxisItem).id, (measures[1] as IAxisItem).id]
                    }, {
                        key: getOutputParamsKeyFromField((staticProperties.levelsStructure.rows[0] as IAxisItem)),
                        value: label
                    }]
                }
            }

            data.push(nodeDefinition);
            rowIndex += 1;
        });

        // create the events for the series
        const hcEvents: any = {};
        if (linkOptions && linkOptions.events) {

            const mouseClickEventHandler = linkOptions.events.click;
            if (mouseClickEventHandler) {
                hcEvents.click = (event: any) => {
                    const parameters = event.point.series.userOptions.linkOutputParams;
                    const eventData : any = {
                        Measures: parameters.measures
                    }
                    const { row } = parameters;
                    eventData[row] = event.point.name;
                    mouseClickEventHandler(event, eventData, undefined, undefined);
                };
            }

            const mouseOverEventHandler = linkOptions.events.mouseover;
            if (mouseOverEventHandler) {
                hcEvents.mouseOver = (event: any) => {
                    const parameters = event.target.series.userOptions.linkOutputParams;
                    const eventData : any = {
                        Measures: parameters.measures
                    }
                    const { row } = parameters;
                    eventData[row] = event.target.name;
                    mouseOverEventHandler(event, eventData, undefined, undefined);
                };
            }
            const mouseOutEventHandler = linkOptions.events.mouseout;
            if (mouseOutEventHandler) {
                hcEvents.mouseOut = (event: any) => {
                    const parameters = event.target.series.userOptions.linkOutputParams;
                    const eventData : any = {
                        Measures: parameters.measures
                    }
                    const { row } = parameters.row;
                    eventData[row] = event.target.name;
                    mouseOutEventHandler(event, eventData, undefined, undefined);
                };
            }
        }

        const linkOutputParams = {
            measures: measures.map((measure : any) => {
                return getOutputParamFromMeasure(measure);
            }),
            row: getOutputParamsKeyFromField(dataset.summary.rows.items[0])
        };

        this.recordMinMaxValues(min, max);

        return [
            {
                borderWidth: 1,
                dataLabels: {
                    enabled: false
                },
                data,
                point: {
                    events: hcEvents,
                },
                linkOutputParams,
                layoutAlgorithm: 'squarified',
                chartType: 'treemap',
                allowTraversingTree: true,
                levels: [
                    {
                        level: 1,
                        dataLabels: {
                            enabled: true
                        },
                        borderWidth: 3
                    }
                ]
            }
        ];
    }
}

export const DualMeasureTreeMapVisualizationDescriptor = {
    legacyAdhoc: 'dual_measure_tree_map',
    parentClass: 'Range',
    cssClass: 'DualMeasureTreeMap',
    bundleName: 'adhoc.visualisation.chooser.type.DUAL_MEASURE_TREE_MAP',
    name: 'DualMeasureTreeMap',
    group: 'adhoc.visualisation.chooser.type.GROUP_RANGE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: DualMeasureTreeMapVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    requirements: {
        measures: {
            min: 2,
            max: 2,
            inRow: false
        },
        fields: {
            min: 1,
            max: 1,
            inColumn: false
        }
    }
};
